import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Button, Dialog, SizableText, VisuallyHidden, XStack, YStack } from 'tamagui';
import { BodyText, CardTitle, EyebrowText, SectionTitle } from '@/components/ui/Typography';

const ACCENT = '#E4572E';
const MUTED = '#5C5854';
const BORDER = 'rgba(13,13,13,0.08)';

function initials(email: string) {
  return (email?.[0] ?? '?').toUpperCase();
}

function Avatar({ email, bg }: { email: string; bg: string }) {
  return (
    <View
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: bg,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SizableText size="$5" fontWeight="700" color="white" lineHeight={40}>
        {initials(email)}
      </SizableText>
    </View>
  );
}

function AccountRow({ email, label, bg }: { email: string; label: string; bg: string }) {
  return (
    <XStack ai="center" gap={12}>
      <Avatar email={email} bg={bg} />
      <YStack gap={1}>
        <EyebrowText color={MUTED} fontWeight="500" letterSpacing={0.2}>
          {label}
        </EyebrowText>
        <CardTitle color="#0D0D0D" numberOfLines={1}>
          {email}
        </CardTitle>
      </YStack>
    </XStack>
  );
}

export type SSOAccountSwitchModalProps = {
  open: boolean;
  currentEmail: string;
  targetEmail: string;
  busy: boolean;
  onConfirm: () => void;
  onStay: () => void;
};

export function SSOAccountSwitchModal({
  open,
  currentEmail,
  targetEmail,
  busy,
  onConfirm,
  onStay,
}: SSOAccountSwitchModalProps) {
  return (
    <Dialog modal open={open} onOpenChange={(next) => !busy && !next && onStay()}>
      <Dialog.Portal zIndex={500}>
        <Dialog.Overlay
          key="sso-overlay"
          animation="quick"
          opacity={0.5}
          backgroundColor="rgba(0,0,0,0.5)"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          zIndex={500}
        />
        <Dialog.Content
          elevate
          key="sso-content"
          zIndex={500}
          bordered
          animation={['quick', { opacity: { overshootClamping: true } }]}
          enterStyle={{ y: 10, opacity: 0, scale: 0.97 }}
          exitStyle={{ y: 4, opacity: 0, scale: 0.98 }}
          backgroundColor="#FFFFFF"
          borderColor={BORDER}
          borderRadius={24}
          padding={0}
          width="92%"
          maxWidth={400}
          onPress={(e) => e.stopPropagation()}
        >
          <VisuallyHidden>
            <Dialog.Title>Switch account</Dialog.Title>
          </VisuallyHidden>

          <YStack
            paddingHorizontal={24}
            paddingTop={28}
            paddingBottom={20}
            gap={6}
            borderBottomWidth={1}
            borderBottomColor={BORDER}
          >
            <SectionTitle color="#0D0D0D" letterSpacing={-0.2}>
              Switch account?
            </SectionTitle>
            <BodyText color={MUTED}>
              A different Open Studio account was detected.
            </BodyText>
          </YStack>

          <YStack paddingHorizontal={24} paddingVertical={20} gap={14}>
            <AccountRow email={currentEmail} label="SIGNED IN AS" bg="#B3B3B3" />

            <XStack ai="center" gap={10}>
              <View style={{ flex: 1, height: 1, backgroundColor: BORDER }} />
              <SizableText size="$3" color={MUTED}>↓</SizableText>
              <View style={{ flex: 1, height: 1, backgroundColor: BORDER }} />
            </XStack>

            <AccountRow email={targetEmail} label="SWITCH TO" bg={ACCENT} />
          </YStack>

          <YStack
            paddingHorizontal={20}
            paddingBottom={24}
            paddingTop={4}
            gap={10}
          >
            <Button
              size="$4"
              borderRadius={14}
              backgroundColor={ACCENT}
              color="white"
              fontWeight="700"
              borderWidth={0}
              pressStyle={{ opacity: 0.88, scale: 0.99 }}
              disabled={busy}
              onPress={onConfirm}
            >
              {busy ? (
                <XStack ai="center" jc="center" gap={8}>
                  <ActivityIndicator color="white" size="small" />
                  <SizableText color="white" size="$4" fontWeight="700">Switching…</SizableText>
                </XStack>
              ) : (
                'Switch account'
              )}
            </Button>

            <Button
              size="$4"
              borderRadius={14}
              borderWidth={1}
              borderColor={BORDER}
              backgroundColor="white"
              color={MUTED}
              fontWeight="600"
              pressStyle={{ opacity: 0.7 }}
              disabled={busy}
              onPress={onStay}
            >
              Stay signed in
            </Button>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
